// @ts-check
import React from 'react';
import { List, ListItem } from '@material-ui/core';
import { Virtuoso } from 'react-virtuoso';

/**
 * @typedef RenderListItemProps
 * @property {number} index The index of the item
 * @property {Object} item Data object for the list item
 * @property {Object} virtuosoProps The rest of the props which should be applied to the list item
 */

/**
 * @typedef VirtuosoListProps
 * @property {Object[]} data
 * @property {function(RenderListItemProps): JSX.Element} renderListItem
 * @property {number} overscan
 * @property {number} defaultItemHeight
 */

/**
 * A wrapper around Virtuoso to work with MUI List
 *
 * Built from the example at https://virtuoso.dev/material-ui-endless-scrolling/
 *
 * This is hard to write tests for because it wont render items in the simulated jsdom
 *   Virtuoso 3 adds a mock helper: https://virtuoso.dev/mocking-in-tests/
 *
 * @param {VirtuosoListProps} props
 * @returns {JSX.Element}
 */
const VirtuosoList = ({ data, renderListItem, overscan = 300, defaultItemHeight }) => {
  // This will be passed into Virtuso as components to be used in place of the defaults
  // useMemo because Virtuso will stutter if this changes often
  const MUIComponents = React.useMemo(
    () => ({
      // @ts-ignore
      List: React.forwardRef(({ style, children }, listRef) => {
        return (
          <List style={{ padding: 0, ...style }} ref={listRef}>
            {children}
          </List>
        );
      }),

      Item: ({ children = null, ...virtuosoProps }) => {
        // Pass the props straight to the item without a wrapper
        //  This works with renderItem below so we don't need an extra wrapper on our items
        if (typeof children === 'function') return children(virtuosoProps);

        return <ListItem {...virtuosoProps}>{children}</ListItem>;
      },
    }),
    [],
  );

  const renderItem = (index, item) => {
    return (virtuosoProps) => renderListItem({ index, item, virtuosoProps });
  };

  return (
    <Virtuoso
      data={data}
      itemContent={renderItem}
      components={MUIComponents}
      overscan={overscan}
      defaultItemHeight={defaultItemHeight}
    />
  );
};

export default VirtuosoList;
